import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import "./Audit.css";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CONTACT_URL = "https://www.oxylog.fr/contact/";
const ADMIN_KEY_STORAGE = "oxylog_admin_key";
// En-tête applicatif : identifie les appels issus du frontend (une URL tapée dans un navigateur renvoie 403).
const APP_HEADERS = { "X-Audit-Client": "oxylog-audit-web" };
const viewTokenKey = (id) => `oxylog_audit_view_${id}`;

// Charge le rapport : dossier complet si une clé admin valide est mémorisée (équipe Oxylog),
// sinon l'aperçu public (prospect) — les recommandations au-delà des 3 premières restent masquées côté serveur.
async function fetchReport(id) {
  const key = localStorage.getItem(ADMIN_KEY_STORAGE);
  if (key) {
    const r = await fetch(`${API_BASE}/audits/${id}`, { headers: { "X-Admin-Key": key } });
    if (r.ok) {
      const full = await r.json();
      return { ...full, hidden_count: 0, total_recommendations: full.recommendations.length, is_admin: true };
    }
    if (r.status === 404) throw new Error("notfound");
  }
  const token = localStorage.getItem(viewTokenKey(id)) || "";
  const r = await fetch(`${API_BASE}/audit/${id}`, { headers: { ...APP_HEADERS, "X-View-Token": token } });
  if (r.status === 403) throw new Error("forbidden");
  if (!r.ok) throw new Error("notfound");
  return { ...(await r.json()), is_admin: false };
}

// Détection dynamique du préfixe de montage : standalone (racine → "") ou portail preview ("/audit").
// Évaluée à chaque appel pour rester correcte même si le bundle est chargé avant d'atteindre /audit
// (cas de l'iframe preview qui ouvre d'abord "/").
export function getAuditBase() {
  if (typeof window === "undefined") return "";
  return window.location.pathname.startsWith("/audit") ? "/audit" : "";
}
function getHomePath() {
  return getAuditBase() || "/";
}

function lvl(score) {
  if (score >= 80) return "high";
  if (score >= 50) return "mid";
  return "low";
}

export function AuditHeader() {
  return (
    <header className="audit-header" data-testid="audit-header">
      <div className="audit-header-inner">
        <Link to={getHomePath()} className="audit-logo" data-testid="audit-logo">
          <span>Oxylog</span><span className="audit-logo-dot">·</span><span>Audit IT</span>
        </Link>
        <a href="https://www.oxylog.fr" className="audit-header-cta" data-testid="audit-back-site">
          Retour au site →
        </a>
      </div>
    </header>
  );
}

/* ============================================================
   LANDING — page intro
   ============================================================ */
export function AuditLanding() {
  const navigate = useNavigate();
  return (
    <div className="audit-app">
      <AuditHeader />
      <div className="audit-container">
        <div className="audit-intro">
          <span className="audit-intro-badge">◆ Diagnostic gratuit · 8 min</span>
          <h1>Évaluez la maturité<br /><em>de votre infrastructure IT</em></h1>
          <p className="audit-intro-lead">
            Répondez à 38 questions claires sur vos postes, serveurs, sécurité, sauvegardes, M365,
            conformité et gouvernance. Recevez ensuite <strong>votre score global</strong>,
            un <strong>diagnostic visuel par domaine</strong> et un <strong>rapport
            détaillé de recommandations priorisées</strong>, envoyé <strong>par email
            sous 24-48h</strong> par un expert Oxylog — gratuitement, sans engagement.
          </p>

          <div className="audit-intro-grid" data-testid="audit-intro-stats">
            <div className="audit-intro-stat"><strong>38</strong><span>questions claires &amp; rapides</span></div>
            <div className="audit-intro-stat"><strong>8 min</strong><span>de temps de réponse moyen</span></div>
            <div className="audit-intro-stat"><strong>8</strong><span>domaines couverts</span></div>
            <div className="audit-intro-stat"><strong>100 %</strong><span>gratuit &amp; confidentiel</span></div>
          </div>

          <div className="audit-intro-domains" data-testid="audit-intro-domains">
            <div className="audit-intro-domain">Postes &amp; parc IT</div>
            <div className="audit-intro-domain">Serveurs &amp; infrastructure</div>
            <div className="audit-intro-domain">Réseau &amp; connectivité</div>
            <div className="audit-intro-domain">Cybersécurité</div>
            <div className="audit-intro-domain">Sauvegardes &amp; PRA</div>
            <div className="audit-intro-domain">Microsoft 365</div>
            <div className="audit-intro-domain">Conformité (NIS2, RGPD)</div>
            <div className="audit-intro-domain">Gouvernance IT</div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="audit-btn audit-btn-primary" data-testid="audit-start-btn"
              onClick={() => navigate(`${getAuditBase()}/diagnostic`)}>
              Commencer l'audit gratuit →
            </button>
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer"
              className="audit-btn audit-btn-secondary" data-testid="audit-rdv-btn">
              Préférer un échange avec un expert
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   DIAGNOSTIC — wizard
   ============================================================ */
export function AuditDiagnostic() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    full_name: "", company: "", email: "", phone: "", role: "", headcount: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/audit/catalog`, { headers: APP_HEADERS })
      .then((r) => { if (!r.ok) throw new Error("http"); return r.json(); })
      .then(setCatalog)
      .catch(() => setError("Impossible de charger le catalogue. Vérifiez votre connexion."));
  }, []);

  const stepTotal = catalog ? catalog.domains.length + 1 : 0;
  const onContactStep = catalog && stepIndex >= catalog.domains.length;

  const currentDomain = catalog && !onContactStep ? catalog.domains[stepIndex] : null;
  const currentQuestions = useMemo(() => {
    if (!catalog || !currentDomain) return [];
    return catalog.questions.filter((q) => q.domain === currentDomain.key);
  }, [catalog, currentDomain]);

  const currentDomainComplete = useMemo(() => {
    if (!currentQuestions.length) return false;
    return currentQuestions.every((q) => answers[q.id] !== undefined);
  }, [currentQuestions, answers]);

  const totalAnswered = catalog ? catalog.questions.filter((q) => answers[q.id] !== undefined).length : 0;
  const totalQuestions = catalog ? catalog.questions.length : 0;
  const progress = catalog ? Math.round(((stepIndex + (onContactStep ? 0 : currentDomainComplete ? 1 : 0.5)) / stepTotal) * 100) : 0;

  const contactValid =
    contact.full_name.trim().length >= 2 &&
    contact.company.trim().length >= 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email);

  function selectAnswer(qid, optIdx) {
    setAnswers((prev) => ({ ...prev, [qid]: optIdx }));
  }

  function goNext() {
    if (stepIndex < catalog.domains.length - 1) {
      setStepIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (stepIndex === catalog.domains.length - 1) {
      setStepIndex(catalog.domains.length);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
  function goPrev() {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  async function submitAudit() {
    if (!contactValid || totalAnswered < totalQuestions) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/audit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...APP_HEADERS },
        body: JSON.stringify({ contact, answers }),
      });
      if (!res.ok) throw new Error("Échec de l'enregistrement");
      const data = await res.json();
      if (data.view_token) localStorage.setItem(viewTokenKey(data.id), data.view_token);
      navigate(`${getAuditBase()}/rapport/${data.id}`);
    } catch (e) {
      setError("Une erreur est survenue lors de l'envoi. Merci de réessayer.");
      setSubmitting(false);
    }
  }

  if (!catalog) {
    return (
      <div className="audit-app">
        <AuditHeader />
        <div className="audit-container" data-testid="audit-loading">
          <p style={{ color: "#6B6B6B" }}>Chargement du diagnostic…</p>
          {error && <p style={{ color: "#E84D4D", marginTop: 12 }}>{error}</p>}
        </div>
      </div>
    );
  }

  const currentStepLabel = onContactStep ? "Vos coordonnées" : currentDomain.label;
  const stepDisplay = stepIndex + 1;

  return (
    <div className="audit-app">
      <AuditHeader />
      <div className="audit-progress-bar" data-testid="audit-progress">
        <div className="audit-progress-inner">
          <div className="audit-progress-info">
            <span>Étape <strong>{stepDisplay}</strong> / {stepTotal} · <strong>{currentStepLabel}</strong></span>
            <span>{totalAnswered}/{totalQuestions} questions</span>
          </div>
          <div className="audit-progress-track">
            <div className="audit-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="audit-container">
        {!onContactStep ? (
          <div className="audit-domain-card" data-testid={`audit-domain-${currentDomain.key}`}>
            <span className="audit-domain-tag">Domaine {stepDisplay} / {catalog.domains.length}</span>
            <h2 className="audit-domain-title">{currentDomain.label}</h2>

            {currentQuestions.map((q) => (
              <div key={q.id} className="audit-q" data-testid={`audit-q-${q.id}`}>
                <div className="audit-q-label">{q.label}</div>
                <div className="audit-q-options">
                  {q.options.map((opt, idx) => {
                    const selected = answers[q.id] === idx;
                    return (
                      <label
                        key={idx}
                        className={`audit-q-option${selected ? " is-selected" : ""}`}
                        data-testid={`audit-q-${q.id}-opt-${idx}`}
                        onClick={() => selectAnswer(q.id, idx)}
                      >
                        <span className="audit-q-radio" aria-hidden="true" />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="audit-domain-card" data-testid="audit-contact-card">
            <span className="audit-domain-tag">Dernière étape</span>
            <h2 className="audit-domain-title">Recevez votre diagnostic personnalisé</h2>
            <p style={{ fontSize: 14.5, color: "#6B6B6B", lineHeight: 1.65, margin: "0 0 20px" }}>
              Indiquez-nous vos coordonnées : un expert Oxylog vous transmettra votre rapport
              détaillé par email sous 24 à 48h. Aucun engagement, vos données restent confidentielles.
            </p>

            <div className="audit-form-grid">
              <div className="audit-form-field">
                <label htmlFor="audit-name">Nom complet *</label>
                <input id="audit-name" data-testid="audit-input-name" type="text" value={contact.full_name}
                  onChange={(e) => setContact({ ...contact, full_name: e.target.value })} />
              </div>
              <div className="audit-form-field">
                <label htmlFor="audit-company">Société *</label>
                <input id="audit-company" data-testid="audit-input-company" type="text" value={contact.company}
                  onChange={(e) => setContact({ ...contact, company: e.target.value })} />
              </div>
              <div className="audit-form-field">
                <label htmlFor="audit-email">Email professionnel *</label>
                <input id="audit-email" data-testid="audit-input-email" type="email" value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              </div>
              <div className="audit-form-field">
                <label htmlFor="audit-phone">Téléphone</label>
                <input id="audit-phone" data-testid="audit-input-phone" type="tel" value={contact.phone}
                  onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              </div>
              <div className="audit-form-field">
                <label htmlFor="audit-role">Fonction</label>
                <input id="audit-role" data-testid="audit-input-role" type="text" placeholder="DG, DAF, DSI…" value={contact.role}
                  onChange={(e) => setContact({ ...contact, role: e.target.value })} />
              </div>
              <div className="audit-form-field">
                <label htmlFor="audit-headcount">Effectif</label>
                <select id="audit-headcount" data-testid="audit-input-headcount" value={contact.headcount}
                  onChange={(e) => setContact({ ...contact, headcount: e.target.value })}>
                  <option value="">— Sélectionner —</option>
                  <option value="1-9">Moins de 10</option>
                  <option value="10-49">10 à 49</option>
                  <option value="50-249">50 à 249</option>
                  <option value="250+">250 et plus</option>
                </select>
              </div>
            </div>
            <p className="audit-form-mention">
              Vos données sont stockées de manière confidentielle et utilisées exclusivement pour la production du rapport.
              Aucun envoi à un tiers, vous pouvez demander leur suppression à tout moment.
            </p>
            {error && <p style={{ color: "#E84D4D", marginTop: 12 }}>{error}</p>}
          </div>
        )}

        <div className="audit-step-nav">
          {stepIndex > 0 ? (
            <button className="audit-btn audit-btn-ghost" data-testid="audit-prev-btn" onClick={goPrev}>
              ← Précédent
            </button>
          ) : (
            <span className="audit-step-nav-spacer" />
          )}
          <div className="audit-step-nav-spacer" />
          {!onContactStep ? (
            <button className="audit-btn audit-btn-primary" data-testid="audit-next-btn"
              disabled={!currentDomainComplete} onClick={goNext}>
              Continuer →
            </button>
          ) : (
            <button className="audit-btn audit-btn-primary" data-testid="audit-submit-btn"
              disabled={!contactValid || submitting || totalAnswered < totalQuestions}
              onClick={submitAudit}>
              {submitting ? "Envoi en cours…" : "Recevoir mon rapport par email →"}
            </button>
          )}
        </div>

        {!onContactStep && !currentDomainComplete && (
          <p style={{ marginTop: 12, fontSize: 13, color: "#999", textAlign: "right" }}>
            Répondez à toutes les questions du domaine pour continuer.
          </p>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   RAPPORT — aperçu du résultat + teasing
   Le rapport détaillé complet est envoyé par email par Oxylog
   sous 24-48h ouvrées (qualification lead humaine).
   ============================================================ */
export function AuditReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchReport(id)
      .then(setData)
      .catch((e) => setError(e.message === "forbidden"
        ? "Ce rapport n'est consultable que depuis le navigateur ayant réalisé l'audit. Votre synthèse complète vous est transmise par email."
        : "Rapport introuvable ou expiré."));
  }, [id]);

  const radarData = useMemo(() => {
    if (!data) return [];
    const labels = {
      parc: "Parc IT", infra: "Serveurs", reseau: "Réseau", cyber: "Cyber",
      backup: "Sauvegardes", m365: "M365", conformite: "Conformité", gouvernance: "Gouvernance",
    };
    return Object.entries(data.scores_by_domain).map(([k, v]) => ({
      domain: labels[k] || k, score: v,
    }));
  }, [data]);

  const overall = data?.overall_score ?? 0;
  const overallLvl = lvl(overall);
  const overallVerdict = overall >= 80
    ? "Maturité forte — quelques optimisations restent possibles."
    : overall >= 50
      ? "Maturité moyenne — plusieurs axes d'amélioration prioritaires."
      : "Maturité faible — votre infrastructure présente des risques critiques.";

  if (error) {
    return (
      <div className="audit-app">
        <AuditHeader />
        <div className="audit-container" data-testid="audit-report-error">
          <h2 style={{ fontSize: 28, marginBottom: 10, lineHeight: 1.3 }}>{error}</h2>
          <Link to={getHomePath()} className="audit-btn audit-btn-primary" style={{ marginTop: 16 }}>
            Lancer un nouvel audit
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="audit-app">
        <AuditHeader />
        <div className="audit-container" data-testid="audit-report-loading">
          <p style={{ color: "#6B6B6B" }}>Chargement de votre aperçu…</p>
        </div>
      </div>
    );
  }

  const dateStr = new Date(data.created_at).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "long", year: "numeric",
  });

  const radius = 100;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (overall / 100) * circumference;

  const domainLabels = {
    parc: "Postes & parc IT", infra: "Serveurs & infrastructure", reseau: "Réseau & connectivité",
    cyber: "Cybersécurité", backup: "Sauvegardes & PRA", m365: "Microsoft 365",
    conformite: "Conformité (NIS2, RGPD)", gouvernance: "Gouvernance IT",
  };

  const isAdmin = !!data.is_admin;
  const displayName = data.contact.full_name || data.contact.first_name || "";
  const previewRecos = data.recommendations;
  const hiddenCount = isAdmin ? 0 : (data.hidden_count || 0);
  const totalRecos = data.total_recommendations ?? data.recommendations.length;

  return (
    <div className="audit-app">
      <AuditHeader />
      <div className="audit-container">

        {isAdmin ? (
          <div className="audit-email-notice audit-admin-notice" data-testid="audit-admin-notice">
            <div className="audit-email-notice-icon" aria-hidden="true">★</div>
            <div className="audit-email-notice-body">
              <h3>Mode équipe Oxylog — rapport complet</h3>
              <p>
                Vous consultez le dossier intégral de <strong>{data.contact.company}</strong> ({displayName}
                {data.contact.role ? `, ${data.contact.role}` : ""}{data.contact.phone ? ` · ${data.contact.phone}` : ""}).
                Les {totalRecos} recommandations sont affichées en clair. Le prospect, lui, ne voit que les 3 premières.
              </p>
            </div>
          </div>
        ) : (
        <div className="audit-email-notice" data-testid="audit-email-notice">
          <div className="audit-email-notice-icon" aria-hidden="true">✉</div>
          <div className="audit-email-notice-body">
            <h3>Votre rapport détaillé arrive par email</h3>
            <p>
              Bonjour {data.contact.first_name}, votre audit est <strong>enregistré</strong>.
              Un expert Oxylog analyse actuellement vos réponses et vous transmettra
              votre <strong>rapport complet personnalisé</strong> à
              <strong> {data.contact.email}</strong> sous <strong>24 à 48h ouvrées</strong>,
              accompagné d'un plan de remédiation chiffré.
            </p>
          </div>
        </div>
        )}

        <div className="audit-report-hero" data-testid="audit-report-hero">
          <div className="audit-report-hero-top">
            <div>
              <h2>Aperçu de votre diagnostic<br /><em>{data.contact.company}</em></h2>
              <p>Audit infrastructure réalisé pour {displayName}</p>
            </div>
            <div className="audit-report-meta">
              <strong>{dateStr}</strong>
              Ref. {data.id.substring(0, 8).toUpperCase()}
            </div>
          </div>

          <div className="audit-score-grid">
            <div className="audit-gauge" data-testid="audit-gauge">
              <svg className="audit-gauge-svg" viewBox="0 0 240 240">
                <circle className="audit-gauge-track" cx="120" cy="120" r={radius} />
                <circle className={`audit-gauge-bar lvl-${overallLvl}`} cx="120" cy="120" r={radius}
                  strokeDasharray={circumference} strokeDashoffset={offset} />
              </svg>
              <div className="audit-gauge-center">
                <div className="audit-gauge-value">{overall}<sub>/100</sub></div>
                <div className="audit-gauge-label">Score global</div>
              </div>
            </div>
            <div className="audit-radar-wrap" data-testid="audit-radar">
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} outerRadius="78%">
                  <PolarGrid stroke="rgba(255,255,255,0.18)" />
                  <PolarAngleAxis dataKey="domain" tick={{ fill: "rgba(255,255,255,0.78)", fontSize: 11.5 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar dataKey="score" stroke="#F0940D" fill="#F0940D" fillOpacity={0.30} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <p style={{ marginTop: 18, color: "rgba(255,255,255,0.78)", fontSize: 14.5, lineHeight: 1.6 }}>
            {overallVerdict}
          </p>
        </div>

        <div className="audit-section" data-testid="audit-section-domains">
          <h3 className="audit-section-title">Détail par domaine</h3>
          {Object.entries(data.scores_by_domain).map(([key, score]) => (
            <div key={key} className="audit-domain-row" data-testid={`audit-row-${key}`}>
              <div className="audit-domain-row-name">{domainLabels[key] || key}</div>
              <div className="audit-domain-row-track">
                <div className={`audit-domain-row-fill lvl-${lvl(score)}`} style={{ width: `${score}%` }} />
              </div>
              <div className={`audit-domain-row-score lvl-${lvl(score)}`}>{score}</div>
            </div>
          ))}
        </div>

        {data.recommendations.length > 0 ? (
          <div className="audit-section audit-section-recos-wrap" data-testid="audit-section-recos">
            <h3 className="audit-section-title">
              Recommandations priorisées ({totalRecos})
            </h3>
            {!isAdmin && (
              <p style={{ fontSize: 13.5, color: "#6B6B6B", margin: "0 0 18px" }}>
                Voici un <strong>aperçu des {previewRecos.length} recommandations les plus critiques</strong>.
                L'analyse complète (dont les {hiddenCount > 0 ? hiddenCount : "autres"} recommandations restantes,
                le plan de remédiation et l'estimation budgétaire) vous parviendra par email sous 24-48h.
              </p>
            )}

            {previewRecos.map((reco, idx) => (
              <div key={reco.question_id}
                className={`audit-reco audit-reco-${reco.priority}`}
                data-testid={`audit-reco-${reco.question_id}`}>
                <div className="audit-reco-num">{String(idx + 1).padStart(2, "0")}</div>
                <div className="audit-reco-body">
                  <div className="audit-reco-domain">{reco.domain_label}</div>
                  <div className="audit-reco-q">{reco.question_label}</div>
                  <div className="audit-reco-text">{reco.recommendation}</div>
                  <span className={`audit-reco-badge audit-reco-badge-${reco.priority}`}>
                    {reco.priority === "high" ? "Priorité haute" : "Priorité moyenne"} · Score {reco.score}/100
                  </span>
                </div>
              </div>
            ))}

            {hiddenCount > 0 && (
              <div className="audit-reco-locked" data-testid="audit-reco-locked">
                <div className="audit-reco-locked-blur" aria-hidden="true">
                  {Array.from({ length: Math.min(3, hiddenCount) }).map((_, idx) => (
                    <div key={idx} className="audit-reco audit-reco-medium">
                      <div className="audit-reco-num">{String(previewRecos.length + idx + 1).padStart(2, "0")}</div>
                      <div className="audit-reco-body">
                        <div className="audit-reco-domain">Domaine concerné</div>
                        <div className="audit-reco-q">Recommandation réservée au rapport complet envoyé par email</div>
                        <div className="audit-reco-text">
                          Le détail de cette recommandation, son niveau de priorité et le plan de remédiation
                          associé vous seront transmis par un expert Oxylog.
                        </div>
                        <span className="audit-reco-badge audit-reco-badge-medium">Priorité · Score —/100</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="audit-reco-locked-overlay">
                  <div className="audit-reco-locked-icon" aria-hidden="true">🔒</div>
                  <div className="audit-reco-locked-title">
                    +{hiddenCount} recommandation{hiddenCount > 1 ? "s" : ""} supplémentaire{hiddenCount > 1 ? "s" : ""}
                  </div>
                  <p className="audit-reco-locked-text">
                    L'ensemble de vos recommandations, le plan d'action détaillé et
                    l'estimation budgétaire vous seront envoyés par email par un expert Oxylog
                    sous 24 à 48h ouvrées.
                  </p>
                  <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer"
                    className="audit-btn audit-btn-primary" data-testid="audit-locked-cta">
                    Accélérer via un échange direct →
                  </a>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="audit-section" data-testid="audit-section-no-recos">
            <h3 className="audit-section-title">Aucune recommandation critique</h3>
            <p style={{ color: "#6B6B6B", margin: 0, fontSize: 14.5, lineHeight: 1.65 }}>
              Bravo — votre infrastructure présente une maturité homogène sans point critique majeur.
              Quelques optimisations marginales restent possibles : un échange avec un consultant
              Oxylog permettra d'identifier les leviers les plus rentables.
            </p>
          </div>
        )}

        <div className="audit-cta-final" data-testid="audit-cta-final">
          <h3>Discutons de votre plan<br /><em>de remédiation</em></h3>
          <p>
            Vous préférez ne pas attendre 24-48h ? Prenez rendez-vous directement avec un
            expert Oxylog. Nous vous accompagnons dans la priorisation des chantiers et
            l'estimation budgétaire — sans engagement.
          </p>
          <div className="audit-cta-row">
            <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer"
              className="audit-btn audit-btn-primary" data-testid="audit-cta-rdv">
              Prendre rendez-vous →
            </a>
            <a href="mailto:contact@oxylog.fr" className="audit-btn audit-btn-secondary"
              style={{ borderColor: "rgba(255,255,255,0.40)", color: "#fff" }}
              data-testid="audit-cta-mail">
              Nous écrire
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}
